import { getCSS, useTranslation } from '@gimlite/common';
import classNames from 'classnames';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import ReactSelect, {
  OptionProps,
  OptionsOrGroups,
  SelectInstance,
  components,
} from 'react-select';
import { usePortalZIndex } from '../../hooks/usePortalZIndex';
import { Checkbox } from '../checkbox/checkbox.component';
import { Icon } from '../icon/icon.component';
import { Text } from '../text/text.component';
import './select.component.scss';

export declare namespace SelectType {
  type BaseProps = {
    className?: string | null;
    placeholder?: string | null;
    onBlur?: () => void;
    size?: 'small' | 'medium';
    theme?: 'light' | 'dark';
    disabled?: boolean;
    defaultSortOptions?: 'asc' | 'desc' | null;
    allowDeselect?: boolean;
    dropdownDirection?: 'down' | 'up';
    footerContent?: React.ReactNode | null;
    options: Array<{
      extraLeft?: React.ReactNode | null;
      label: string | React.ReactNode;
      value: string;
      disabled?: boolean;
    }>;
    borderRadius?: {
      left?: boolean | null;
      right?: boolean | null;
    } | null;
  };

  type SingleSelectProps = BaseProps & {
    isMultiple?: false;
    onChange?: (value?: string | null) => void;
    value?: string | null;
    defaultValue?: string | null;
  };

  type MultipleSelectProps = BaseProps & {
    isMultiple: true;
    label?: string | null;
    onChange?: (value?: string[]) => void;
    value?: string[];
    defaultValue?: string[];
  };

  type Props = SingleSelectProps | MultipleSelectProps;
}

export const Select = memo(
  React.forwardRef<
    SelectInstance<
      {
        label: string | React.ReactNode;
        value: string;
        extraLeft?: React.ReactNode | null;
        disabled?: boolean;
      },
      boolean,
      any
    >,
    SelectType.Props
  >(
    (
      {
        className,
        placeholder,
        onChange,
        size = 'medium',
        value,
        defaultValue,
        onBlur,
        options,
        defaultSortOptions = 'asc',
        isMultiple = false,
        disabled = false,
        allowDeselect = false,
        dropdownDirection = 'down',
        theme = 'light',
        footerContent,
        borderRadius = {
          left: true,
          right: true,
        },
        ...restProps
      }: SelectType.Props,
      ref,
    ) => {
      const { t } = useTranslation();
      const id = React.useId();
      const containerRef = useRef<HTMLDivElement | null>(null);
      const selectRef = useRef<SelectInstance<
        {
          label: string;
          value: string;
          extraLeft?: React.ReactNode | null;
          disabled?: boolean;
        },
        boolean,
        any
      > | null>(null);
      const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
      const zIndex = usePortalZIndex(menuIsOpen);

      const selectKey = useMemo(() => {
        return `select-${id}`;
      }, [id]);

      const optionsSorted = useMemo(
        () =>
          !defaultSortOptions
            ? options
            : options.sort((a, b) =>
                defaultSortOptions === 'asc'
                  ? typeof a.label === 'string'
                    ? a.label.localeCompare(b.label as string)
                    : a.value.localeCompare(b.value as string)
                  : typeof b.label === 'string'
                    ? b.label.localeCompare(a.label as string)
                    : b.value.localeCompare(a.value as string),
              ),
        [options, defaultSortOptions],
      );

      useEffect(() => {
        const handlePointerOrMouseDown = (event: Event) => {
          const target = event.target as Node | null;
          if (
            containerRef.current &&
            target &&
            !containerRef.current.contains(target)
          ) {
            setMenuIsOpen(false);
            selectRef.current?.blur?.();
          }
        };
        const handleFocusIn = (event: FocusEvent) => {
          const target = event.target as Node | null;
          if (
            containerRef.current &&
            target &&
            !containerRef.current.contains(target)
          ) {
            setMenuIsOpen(false);
          }
        };

        document.addEventListener('mousedown', handlePointerOrMouseDown, true);
        document.addEventListener(
          'pointerdown',
          handlePointerOrMouseDown,
          true,
        );
        document.addEventListener('touchstart', handlePointerOrMouseDown, true);
        document.addEventListener('focusin', handleFocusIn, true);
        return () => {
          document.removeEventListener(
            'mousedown',
            handlePointerOrMouseDown,
            true,
          );
          document.removeEventListener(
            'pointerdown',
            handlePointerOrMouseDown,
            true,
          );
          document.removeEventListener(
            'touchstart',
            handlePointerOrMouseDown,
            true,
          );
          document.removeEventListener('focusin', handleFocusIn, true);
        };
      }, []);

      return (
        <div
          ref={containerRef}
          style={{
            ['--select-z-index' as any]: zIndex ?? 10000,
          }}
        >
          <ReactSelect
            key={selectKey}
            id={id}
            ref={(node) => {
              // Conserver notre propre ref pour pouvoir déclencher blur()
              selectRef.current = node as any;
              // Propager aussi la ref transmise au composant
              if (typeof ref === 'function') {
                ref(node as any);
              } else if (ref && 'current' in (ref as any)) {
                (ref as any).current = node;
              }
            }}
            defaultValue={
              isMultiple
                ? Array.isArray(defaultValue) && defaultValue.length > 0
                  ? options.filter((option) =>
                      defaultValue.includes(option.value),
                    )
                  : null
                : defaultValue
                  ? options.find((option) => option.value === defaultValue)
                  : null
            }
            value={
              isMultiple
                ? Array.isArray(value) && value.length > 0
                  ? options.filter((option) => value.includes(option.value))
                  : null
                : value
                  ? options.find((option) => option.value === value)
                  : null
            }
            placeholder={placeholder || ''}
            className={classNames(
              'select',
              `select--size--${size}`,
              `select--dropdown-direction--${dropdownDirection}`,
              `select--theme--${theme}`,
              className,
              {
                'select--disabled': disabled,
                'select--isMultiple': isMultiple,
              },
            )}
            isMulti={!!isMultiple as any}
            hideSelectedOptions={false}
            menuIsOpen={menuIsOpen}
            onMenuOpen={() => setMenuIsOpen(true)}
            onMenuClose={() => setMenuIsOpen(false)}
            onFocus={() => setMenuIsOpen(true)}
            menuPlacement={dropdownDirection === 'up' ? 'top' : 'bottom'}
            unstyled
            isDisabled={disabled}
            isSearchable={true}
            backspaceRemovesValue={true}
            classNames={{
              container: (state) => {
                return classNames(
                  'select-container',
                  {
                    'select-container--border-radius--left': borderRadius?.left,
                    'select-container--border-radius--right':
                      borderRadius?.right,
                    'select-container--border-radius--all':
                      borderRadius?.left && borderRadius?.right,
                  },
                  className,
                );
              },
              control: (state) => {
                return classNames('select-control', className);
              },
              valueContainer: (state) => {
                return classNames('select-value-container', className);
              },
              noOptionsMessage: (state) => {
                return classNames('select-no-options-message', className);
              },
              singleValue: (state) => {
                return classNames('select-single-value', className);
              },
              input: (state) => {
                return classNames('select-input', className);
              },
              menu: (state) => {
                return classNames('select-menu', className, {
                  'select-menu--with-footer': !!footerContent,
                });
              },
              dropdownIndicator: (state) => {
                return classNames('select-dropdown-indicator', className);
              },
              indicatorsContainer: (state) => {
                return classNames('select-indicators-container', className);
              },
              indicatorSeparator: (state) => {
                return classNames('select-indicator-separator', className);
              },
              menuList: (state) => {
                return classNames('select-menu-list', className);
              },
              menuPortal: (state) => {
                return classNames('select-menu-portal', className);
              },
              option: (state) => {
                return classNames(
                  'select-option',
                  {
                    'select-option--disabled':
                      'disabled' in (state.data as any) &&
                      (state.data as any).disabled === true,
                  },
                  className,
                );
              },
              multiValue: (state) => {
                return classNames('select-multi-value', className);
              },
              multiValueLabel: (state) => {
                return classNames('select-multi-value-label', className);
              },
              multiValueRemove: (state) => {
                return classNames('select-multi-value-remove', className);
              },
              clearIndicator: (state) => {
                return classNames('select-clear-indicator', className);
              },
              placeholder: (state) => {
                return classNames('select-placeholder', className);
              },
              loadingMessage: (state) => {
                return classNames('select-loading-message', className);
              },
            }}
            components={{
              MenuList: (props) => {
                return (
                  <components.MenuList {...props}>
                    {props.children}
                    {footerContent && (
                      <div className="select__component--menu-footer">
                        {footerContent}
                      </div>
                    )}
                  </components.MenuList>
                );
              },
              DropdownIndicator: () => (
                <div className="select__component--dropdownIndicator">
                  <Icon
                    variant="dropdown"
                    color={theme === 'dark' ? 'white_100' : 'deep_indigo'}
                  />
                </div>
              ),
              ClearIndicator: (props) => {
                if (!isMultiple) {
                  return <components.ClearIndicator {...props} />;
                }

                return (
                  <div
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      props.clearValue();
                    }}
                    style={{
                      backgroundColor:
                        theme === 'dark'
                          ? getCSS('--color-white_100')
                          : getCSS('--color-deep_indigo'),
                    }}
                    className="select__component--clearIndicator"
                  >
                    <Text
                      variant="headcaption"
                      color={theme === 'dark' ? 'deep_indigo' : 'white_100'}
                    >
                      {props.getValue()?.length}
                    </Text>
                    <Icon
                      variant="close"
                      color={theme === 'dark' ? 'deep_indigo' : 'white_100'}
                      size="xs"
                    />
                  </div>
                );
              },
              // Input: (props) => {
              //   return <components.Input {...props} />;
              // },
              // SingleValue: (props) => {
              //   return <components.SingleValue {...props} />;
              // },

              ...(isMultiple || options.find((option) => option.extraLeft)
                ? {
                    ValueContainer: (props) => {
                      if (!isMultiple) {
                        const selectedOption = options.find(
                          (opt) => opt.value === value,
                        );

                        return (
                          <components.ValueContainer
                            className={classNames(
                              'select__component--valueContainer',
                              'select__component--valueContainer--single',
                              props.className,
                            )}
                            {...props}
                          >
                            {selectedOption?.extraLeft && (
                              <div className="select__component--valueContainer__extraLeft">
                                {selectedOption?.extraLeft}
                              </div>
                            )}
                            {props.children}
                          </components.ValueContainer>
                        );
                      }

                      return (
                        <div
                          className={classNames(
                            'select__component--valueContainer',
                            'select__component--valueContainer--multiple',
                            props.className,
                          )}
                        >
                          <Text
                            variant="subhead"
                            color={theme === 'dark' ? 'white_100' : 'black_70'}
                          >
                            {'label' in restProps && restProps.label
                              ? restProps.label
                              : t('watermelon-selectLabels')}
                          </Text>
                          <div className="select__component--valueContainer__children">
                            {props.children}
                          </div>
                        </div>
                      );
                    },
                  }
                : {}),

              Option: (props: OptionProps<any>) => {
                const typedData = props.data as {
                  label: string | React.ReactNode;
                  value: string;
                  extraLeft?: React.ReactNode | null;
                  disabled?: boolean;
                };
                const isDisabled = !!typedData.disabled;

                return (
                  <div
                    onClick={() => {
                      if (isDisabled) return;
                      if (isMultiple) {
                        if (props.isSelected) {
                          const currentValues = (props.getValue?.() ||
                            []) as Array<{
                            label: string | React.ReactNode;
                            value: string;
                            extraLeft?: React.ReactNode | null;
                            disabled?: boolean;
                          }>;
                          const nextValues = currentValues.filter(
                            (opt) => opt.value !== typedData.value,
                          );
                          props.setValue?.(
                            nextValues as any,
                            'deselect-option',
                          );
                        } else {
                          props.selectOption(typedData);
                        }
                      } else {
                        if (props.isSelected && allowDeselect) {
                          const currentValues = (props.getValue?.() ||
                            []) as Array<{
                            label: string | React.ReactNode;
                            value: string;
                            disabled?: boolean;
                          }>;
                          const nextValues = currentValues.filter(
                            (opt) => opt.value !== typedData.value,
                          );
                          props.setValue?.(
                            nextValues as any,
                            'deselect-option',
                          );
                        } else if (!props.isSelected) {
                          props.selectOption(typedData);
                        }
                      }
                    }}
                    className={classNames(
                      props.className || 'select-option',
                      'select__component--option',
                      {
                        'select-option--disabled': isDisabled,
                      },
                    )}
                  >
                    {isMultiple && (
                      <Checkbox
                        checked={props.isSelected}
                        onChange={() => {
                          if (isDisabled) return;
                          if (props.isSelected) {
                            const currentValues = (props.getValue?.() ||
                              []) as Array<{
                              label: string | React.ReactNode;
                              value: string;
                              extraLeft?: React.ReactNode | null;
                              disabled?: boolean;
                            }>;
                            const nextValues = currentValues.filter(
                              (opt) => opt.value !== typedData.value,
                            );
                            props.setValue?.(
                              nextValues as any,
                              'deselect-option',
                            );
                          } else {
                            props.selectOption(typedData);
                          }
                        }}
                      />
                    )}

                    {typedData.extraLeft}

                    <Text variant="body" color={'black_70'}>
                      {props.label}
                    </Text>
                  </div>
                );
              },
              MultiValueContainer: (props) => {
                if (!isMultiple) {
                  return <components.MultiValueContainer {...props} />;
                }

                return null;
              },
            }}
            noOptionsMessage={() => t('watermelon-noResult')}
            onChange={(value) => {
              if (isMultiple) {
                const selectedValues = Array.isArray(value)
                  ? value
                      .map((option) => option.value)
                      .filter((value): value is string => Boolean(value))
                  : [];
                (onChange as (value: string[]) => void)?.(selectedValues);
              } else {
                const singleValue = value as {
                  label: string | React.ReactNode;
                  value: string;
                  extraLeft?: React.ReactNode | null;
                } | null;
                (onChange as (value?: string | null) => void)?.(
                  singleValue?.value || null,
                );
              }
            }}
            closeMenuOnSelect={!isMultiple}
            onBlur={() => {
              setMenuIsOpen(false);
              onBlur?.();
            }}
            options={
              optionsSorted.map((option) => ({
                label: option.label,
                value: option.value,
                extraLeft: option.extraLeft,
                disabled: !!option.disabled,
              })) as OptionsOrGroups<
                {
                  label: string | React.ReactNode;
                  value: string;
                  extraLeft?: React.ReactNode | null;
                  disabled?: boolean;
                },
                any
              >
            }
          />
        </div>
      );
    },
  ),
);
